// frontend/src/interactions.js

import httprequest from './../axios';

// Interaction types for easy reference
export const INTERACTION_TYPES = {
  FAVORITE_ADD: 'FAVORITE_ADD',
  CUSTOM_LIST_ADD: 'CUSTOM_LIST_ADD',
  GAME_CLICK: 'GAME_CLICK',
  SEARCH_SELECT: 'SEARCH_SELECT',
  TIME_SPENT: 'TIME_SPENT',
  VIEW_DETAILS: 'VIEW_DETAILS',
  HIDE_GAME: 'HIDE_GAME',
  BLACKLIST_ADD: 'BLACKLIST_ADD',
  DISLIKE: 'DISLIKE',
  IGNORE_RECOMMENDATION: 'IGNORE_RECOMMENDATION',
  RECOMMENDATION_VIEW: 'RECOMMENDATION_VIEW',
  RECOMMENDATION_HELPFUL: 'RECOMMENDATION_HELPFUL',
  RECOMMENDATION_NOT_HELPFUL: 'RECOMMENDATION_NOT_HELPFUL',
  BLACKLIST_REMOVE: 'BLACKLIST_REMOVE'
};

/**
 * Record a single user interaction
 */
export const recordInteraction = async (gameId, interactionType, context = {}, additionalScore = 0) => {
  try {
    const response = await httprequest.post('/interactions/record', {
      gameId,
      interactionType,
      context,
      additionalScore
    });
    return response.data;
  } catch (error) {
    console.error('Error recording interaction:', error);
    return null;
  }
};

/**
 * Record multiple interactions at once
 */
export const batchRecordInteractions = async (interactions) => {
  try {
    const response = await httprequest.post('/interactions/batch', { interactions });
    return response.data;
  } catch (error) {
    console.error('Error batch recording interactions:', error);
    return null;
  }
};

/**
 * Get user's interaction history
 */
export const getInteractionHistory = async (limit = 50, offset = 0, interactionType = null) => {
  try {
    let url = `/interactions/history?limit=${limit}&offset=${offset}`;
    if (interactionType) {
      url += `&interactionType=${interactionType}`;
    }
    const response = await httprequest.get(url);
    return response.data;
  } catch (error) {
    console.error('Error getting interaction history:', error);
    return { interactions: [], total: 0 };
  }
};

/**
 * Get user's recommendation preferences
 */
export const getUserPreferences = async () => {
  try {
    const response = await httprequest.get('/preferences');
    return response.data.preferences;
  } catch (error) {
    console.error('Error getting user preferences:', error);
    return null;
  }
};

/**
 * Update user preferences manually
 */
export const updateUserPreferences = async () => {
  try {
    const response = await httprequest.post('/preferences/update');
    return response.data.preferences;
  } catch (error) {
    console.error('Error updating user preferences:', error);
    return null;
  }
};

/**
 * Personalized search
 */
export const personalizedSearch = async (params, page = 1, limit = 20) => {
  try {
    const queryParams = new URLSearchParams({
      page,
      limit,
      ...params
    });
    const response = await httprequest.get(`/search/personalized?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Error in personalized search:', error);
    return { games: [], total: 0 };
  }
};

/**
 * Get personalized recommendations
 */
export const getPersonalizedRecommendations = async (limit = 10, excludeGameIds = []) => {
  try {
    const params = new URLSearchParams({ limit });
    if (excludeGameIds.length > 0) {
      excludeGameIds.forEach(id => params.append('excludeGameIds', id));
    }
    const response = await httprequest.get(`/recommendations/personalized?${params}`);
    return response.data.recommendations;
  } catch (error) {
    console.error('Error getting personalized recommendations:', error);
    return [];
  }
};

/**
 * Get personalized trending games
 */
export const getPersonalizedTrending = async (limit = 20) => {
  try {
    const response = await httprequest.get(`/trending/personalized?limit=${limit}`);
    return response.data.trending;
  } catch (error) {
    console.error('Error getting personalized trending:', error);
    return [];
  }
};

/**
 * Get similar to favorites recommendations
 */
export const getSimilarToFavorites = async (limit = 10) => {
  try {
    const response = await httprequest.get(`/recommendations/similar-to-favorites?limit=${limit}`);
    return response.data.similarGames;
  } catch (error) {
    console.error('Error getting similar to favorites:', error);
    return [];
  }
};

/**
 * Get user's negative games (blacklist, hidden, disliked)
 */
export const getNegativeGames = async () => {
  try {
    const response = await httprequest.get('/games/negative');
    return response.data.negativeGames;
  } catch (error) {
    console.error('Error getting negative games:', error);
    return [];
  }
};

/**
 * Get user's positive games (high engagement)
 */
export const getPositiveGames = async (limit = 20) => {
  try {
    const response = await httprequest.get(`/games/positive?limit=${limit}`);
    return response.data.positiveGames;
  } catch (error) {
    console.error('Error getting positive games:', error);
    return [];
  }
};